import React from 'react'
import { DIAGNOSTIC_META, summarizeDiagnostics } from '../lib/sstDiagnostics'

/* ============================================================================
   DiagnosticsModal — modal vistoso con las validaciones deterministas del SST.
   Se usa en el admin (Revisión Dev / generador). Es autocontenido: sin CoreUI,
   solo React + estilos inline, para no depender de nada del layout.

   Props:
     open         boolean
     onClose      () => void
     diagnostics  [{ code, severity, message, sourceLine, raw, ... }]
     title        string (opcional)
   ============================================================================ */

const SEV = {
  error: { bg: '#fee2e2', fg: '#b91c1c', bd: '#fca5a5', dot: '#ef4444', label: 'Error' },
  warning: { bg: '#fef3c7', fg: '#b45309', bd: '#fcd34d', dot: '#f59e0b', label: 'Warning' },
  info: { bg: '#e0f2fe', fg: '#0369a1', bd: '#7dd3fc', dot: '#0ea5e9', label: 'Info' },
}

function sevOf(d) {
  return SEV[d && d.severity] || SEV.info
}

function diagnosticDependencyName(d) {
  if (!d) return ''
  return d.missingFile || d.file || d.target || ''
}

function diagnosticCanRequestFile(d) {
  return d && (d.code === 'MISSING_CSV_FILE' || d.code === 'MISSING_SPAWN_TARGET')
}

export default function DiagnosticsModal({
  open = false,
  onClose = () => {},
  diagnostics = [],
  title = 'Validaciones del script',
  onRequestFiles = null,
}) {
  if (!open) return null
  const list = Array.isArray(diagnostics) ? diagnostics : []
  const sum = summarizeDiagnostics(list)
  const canRequestFiles = typeof onRequestFiles === 'function' && list.some(diagnosticCanRequestFile)

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 10050, background: 'rgba(15,23,42,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18, backdropFilter: 'blur(2px)' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(720px, 96vw)', maxHeight: '88vh', display: 'flex', flexDirection: 'column', background: '#fff', borderRadius: 16, boxShadow: '0 30px 80px rgba(15,23,42,.45)', overflow: 'hidden' }}
      >
        {/* Header vistoso con gradiente por severidad dominante */}
        <div style={{ padding: '16px 20px', color: '#fff', background: sum.error ? 'linear-gradient(135deg,#ef4444,#b91c1c)' : sum.warning ? 'linear-gradient(135deg,#f59e0b,#b45309)' : 'linear-gradient(135deg,#0ea5e9,#0369a1)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <strong style={{ fontSize: 16 }}>
              {sum.error ? '⛔ ' : sum.warning ? '⚠️ ' : 'ℹ️ '}{title}
            </strong>
            <button type="button" onClick={onClose} aria-label="Cerrar" style={{ border: 0, background: 'rgba(255,255,255,.2)', color: '#fff', width: 30, height: 30, borderRadius: 8, cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <Pill n={sum.error} label="errores" tone="rgba(255,255,255,.25)" />
            <Pill n={sum.warning} label="warnings" tone="rgba(255,255,255,.25)" />
            <Pill n={sum.info} label="info" tone="rgba(255,255,255,.25)" />
          </div>
        </div>

        {/* Cuerpo */}
        <div style={{ padding: 16, overflow: 'auto' }}>
          {list.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#16a34a', padding: '28px 0', fontSize: 15 }}>
              ✅ Sin observaciones deterministas. El script pasó las {Object.keys(DIAGNOSTIC_META).length} validaciones.
            </div>
          ) : (
            list.map((d, i) => {
              const s = sevOf(d)
              const meta = DIAGNOSTIC_META[d.code] || {}
              return (
                <div key={`${d.code}-${d.sourceLine}-${i}`} style={{ border: `1px solid ${s.bd}`, background: s.bg, borderRadius: 12, padding: '10px 12px', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ width: 9, height: 9, borderRadius: '50%', background: s.dot, flex: '0 0 9px' }} />
                    <strong style={{ color: s.fg, fontSize: 13 }}>{meta.title || d.code}</strong>
                    <code style={{ fontSize: 11, color: s.fg, opacity: 0.8 }}>{d.code}</code>
                    <span style={{ marginLeft: 'auto', fontSize: 11, color: s.fg, fontWeight: 700 }}>{s.label}{d.sourceLine ? ` · línea ${d.sourceLine}` : ''}</span>
                  </div>
                  <div style={{ fontSize: 12.5, color: '#334155', marginTop: 4 }}>{d.message}</div>
                  {meta.hint ? <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 2, fontStyle: 'italic' }}>{meta.hint}</div> : null}
                  {d.raw ? <pre style={{ margin: '6px 0 0', padding: '6px 8px', background: 'rgba(15,23,42,.06)', borderRadius: 8, fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#475569' }}>{d.raw}</pre> : null}
                  {diagnosticCanRequestFile(d) && typeof onRequestFiles === 'function' ? (
                    <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        onClick={() => onRequestFiles(d)}
                        style={{ border: `1px solid ${s.bd}`, background: '#fff', color: s.fg, padding: '6px 10px', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 12 }}
                        title={diagnosticDependencyName(d) ? `Cargar ${diagnosticDependencyName(d)}` : 'Cargar archivo requerido'}
                      >
                        Cargar archivo requerido
                      </button>
                    </div>
                  ) : null}
                </div>
              )
            })
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 16px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          {canRequestFiles ? (
            <button type="button" onClick={() => onRequestFiles(null)} style={{ border: '1px solid #93c5fd', background: '#eff6ff', color: '#1d4ed8', padding: '8px 16px', borderRadius: 10, cursor: 'pointer', fontWeight: 700 }}>
              Cargar varios archivos
            </button>
          ) : null}
          <button type="button" onClick={onClose} style={{ border: '1px solid #cbd5e1', background: '#fff', color: '#334155', padding: '8px 16px', borderRadius: 10, cursor: 'pointer', fontWeight: 600 }}>Cerrar</button>
        </div>
      </div>
    </div>
  )
}

function Pill({ n = 0, label = '', tone = 'rgba(255,255,255,.25)' }) {
  return (
    <span style={{ background: tone, color: '#fff', borderRadius: 999, padding: '3px 10px', fontSize: 12, fontWeight: 700 }}>
      {n} {label}
    </span>
  )
}
